import { useQuery, type WatchQueryFetchPolicy } from "@apollo/client";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import { SKILLS_QUERY } from "../../ManageSkills/skillQueries";
import { skillOwner } from "../../SkillAccess/skillAccessTokens";
import { useScoutSkillsLibraryEnabled } from "../../useScoutSkillsLibraryEnabled";
import { type ScoutSkillMenuItem } from "./skillMenuModel";

/**
 * Skills the current user can see in this tenant, shaped for the chat UI.
 * The slash menu and the skill pills share one query (same variables), so
 * the pills read the menu's cached result instead of refetching.
 */
export function useScoutSkillCatalog({
  skip = false,
  fetchPolicy = "cache-first",
}: {
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
} = {}) {
  const { tenantId } = useTenant();
  const enabled = useScoutSkillsLibraryEnabled() && !skip;

  const { data } = useQuery(SKILLS_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      // Scout never activates a disabled skill, so the menu leaves them out.
      enabled: true,
      page: 0,
      pageSize: 100,
      sort: [{ field: "displayName", sort: "asc" }],
    },
    skip: !enabled || !tenantId,
    fetchPolicy,
  });

  const items = useMemo((): ScoutSkillMenuItem[] => {
    return (data?.skills.items ?? []).map((skill) => ({
      id: skill.id,
      slug: skill.slug,
      displayName: skill.displayName,
      description: skill.description,
      owner: skillOwner(skill),
      scope: skill.scope,
      audience: skill.audience,
    }));
  }, [data?.skills.items]);

  return { enabled, items };
}

/**
 * Catalog entry for the skill with this id, or else the first with this slug;
 * undefined when the user can't see it (or it's still loading).
 */
export function useScoutSkill(ref: { id?: string; slug?: string }): ScoutSkillMenuItem | undefined {
  const { id, slug } = ref;
  const { items } = useScoutSkillCatalog({ skip: !id && !slug });
  return useMemo(
    () => (id ? items.find((item) => item.id === id) : items.find((item) => item.slug === slug)),
    [id, items, slug]
  );
}
